import React from 'react';
import { Table } from 'react-bootstrap';

import Card from 'components/DesignSystem/Card';
import SubtitleSemiBold from 'components/Typography/SubtitleSemiBold';
import { useSeriesColor } from 'hooks/theme/useChartTheme';

import './index.scss';

export interface RelatorioTableProps {
  titulo?: string;
  columns: string[];
  rows: (string | number)[][];
  semCard?: boolean;
  // Bolinha colorida antes do rótulo da primeira coluna (ex.: Visão Geral do
  // Intersemanal, ligando cada linha à cor da série homônima no gráfico).
  // Indexado por linha; `undefined` numa posição omite a bolinha naquela linha.
  rowDotColors?: (string | undefined)[];
  // Classe aplicada na 1ª coluna (header e cada linha do body) — ex.: Matriz
  // usa isso pra estilizar a coluna de rótulos (percentis) com fundo/cor
  // diferentes do restante da tabela.
  firstColumnClassName?: string;
}

export function formatNumberBR(n: number): string {
  return n.toLocaleString('pt-BR');
}

export default function RelatorioTable({
  titulo, columns, rows, semCard = false, rowDotColors, firstColumnClassName,
}: RelatorioTableProps) {
  const corSerie = useSeriesColor();
  const tabela = (
    <div className="relatorio-table">
      <Table responsive hover>
        <thead>
          <tr>
            {columns.map((col, cellIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <th key={cellIndex} className={cellIndex === 0 ? firstColumnClassName : undefined}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => {
                const isNegative = typeof cell === 'number' && cell < 0;
                // Bolinha = cor da série no gráfico: mesma adaptação de tema do ThemedECharts.
                const corOriginal = cellIndex === 0 ? rowDotColors?.[rowIndex] : undefined;
                const dotColor = corOriginal && corSerie(corOriginal);
                const className = [
                  isNegative ? 'relatorio-table__neg' : undefined,
                  cellIndex === 0 ? firstColumnClassName : undefined,
                ].filter(Boolean).join(' ') || undefined;
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <td key={cellIndex} className={className}>
                    {dotColor && (
                      <span
                        className="relatorio-table__dot"
                        style={{ backgroundColor: dotColor }}
                      />
                    )}
                    {typeof cell === 'number' ? formatNumberBR(cell) : cell}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  // semCard: usado quando esta tabela ja fica dentro do Card de outra secao
  // (ex.: Fluxos de Intercambio dentro do Card unico de Resultados) — evita
  // Card aninhado dentro de Card.
  if (semCard) {
    return (
      <>
        {titulo && <SubtitleSemiBold>{titulo}</SubtitleSemiBold>}
        {tabela}
      </>
    );
  }

  return (
    <Card title={titulo}>
      {tabela}
    </Card>
  );
}
